import { Fragment, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { transacoesApi, categoriasApi, contasApi, cartoesApi, recorrenciasApi, comprasDivididasApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, AvatarIcon } from '../../components/ui/Card'
import { Badge, DonoBadge } from '../../components/ui/Badge'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Tabs, Pagination, MonthNavigator } from '../../components/ui/Navigation'
import { Table, THead, Th, ThOrdenavel, Tr, Td } from '../../components/ui/Table'
import { diaMes } from '../../lib/corte'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { BotaoFundirLancamento } from './FundirLancamento'
import { BotaoAjusteFatura } from './AjusteFatura'
import { PartesDivisao } from './DivisaoCartoes'
import { partesVazias, partesDaCompra, montarRequest, validarPartes, type ParteForm } from './divisao'
import { useAuthStore } from '../../store/authStore'
import { useContextoStore } from '../../store/contextoStore'
import {
  TransactionType, TransactionStatus, StatusFatura, EscopoParcelas, ModalidadeCartao, MODALIDADE_CARTAO_LABEL,
  TRANSACTION_TYPE_LABEL, TRANSACTION_STATUS_LABEL, TRANSACTION_HISTORY_ACTION_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate, cn } from '../../lib/utils'
import type { CompraCartaoResultado, CompraDividida, FaturaResumo, FinancialTransaction } from '../../types'

type Situacao = 'todas' | 'pendentes' | 'atrasadas' | 'confirmadas'
const NOMES_MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function resumoDaCompra(r: CompraCartaoResultado): string {
  const faturas = r.parcelas.map((p) => `${NOMES_MES[p.faturaMes - 1]}/${String(p.faturaAno).slice(2)} (vence ${formatDate(p.vencimento)})`)
  return r.numParcelas === 1
    ? `Compra lançada na fatura de ${faturas[0]}. O saldo da conta só cai quando a fatura for paga.`
    : `${r.numParcelas}x de ${formatMoney(r.valorTotal / r.numParcelas)} lançadas nas faturas: ${faturas.join(', ')}.`
}

const ESCOPOS: { valor: EscopoParcelas; rotulo: string; ajuda: string }[] = [
  { valor: EscopoParcelas.Apenas, rotulo: 'Só esta parcela', ajuda: 'As outras continuam como estão.' },
  { valor: EscopoParcelas.Proximas, rotulo: 'Esta e as próximas', ajuda: 'Bom para cancelar ou ajustar o resto do parcelamento.' },
  { valor: EscopoParcelas.Todas, rotulo: 'Todas as parcelas', ajuda: 'Parcelas de faturas já pagas ficam como estão.' },
]

const ITENS_POR_PAGINA = 20
const TODOS = 0
/** Opção especial dentro do filtro de cartão: exclui tudo que é de cartão, mostra só movimento de conta. */
const SEM_CARTAO = '__sem_cartao__'
const SEM_CATEGORIA = '__sem_categoria__'

const TABS = [
  { key: TODOS, label: 'Todas', icon: 'ri-list-check-2' },
  { key: TransactionType.Income, label: 'Receitas', icon: 'ri-arrow-left-down-line' },
  { key: TransactionType.Expense, label: 'Despesas', icon: 'ri-arrow-right-up-line' },
  { key: TransactionType.Transfer, label: 'Transferências', icon: 'ri-arrow-left-right-line' },
  { key: TransactionType.Investment, label: 'Investimentos', icon: 'ri-line-chart-line' },
]

const TYPE_VISUAL: Record<TransactionType, { icon: string; tone: 'income' | 'expense' | 'info' | 'warning'; text: string; sign: string }> = {
  [TransactionType.Income]: { icon: 'ri-arrow-left-down-line', tone: 'income', text: 'text-income', sign: '+' },
  [TransactionType.Expense]: { icon: 'ri-arrow-right-up-line', tone: 'expense', text: 'text-expense', sign: '-' },
  [TransactionType.Transfer]: { icon: 'ri-arrow-left-right-line', tone: 'info', text: 'text-info', sign: '' },
  [TransactionType.Investment]: { icon: 'ri-line-chart-line', tone: 'warning', text: 'text-warning', sign: '' },
}

const STATUS_BADGE: Record<TransactionStatus, 'income' | 'warning' | 'expense'> = {
  [TransactionStatus.Confirmed]: 'income',
  [TransactionStatus.Pending]: 'warning',
  [TransactionStatus.Cancelled]: 'expense',
}

const FORM_TYPES: TransactionType[] = [TransactionType.Income, TransactionType.Expense, TransactionType.Transfer, TransactionType.Investment]
const FORM_STATUSES: TransactionStatus[] = [TransactionStatus.Confirmed, TransactionStatus.Pending, TransactionStatus.Cancelled]

interface FormState {
  accountId: string
  destinationAccountId: string
  meioPagamento: 'conta' | 'cartao' | 'dividido'
  /** Compra dividida em cartões: partes e, ao editar, o id da divisão. */
  partes: ParteForm[]
  divisaoId: string
  parcelas: string
  cartaoId: string
  /** Cartão de crédito que também é débito: true = compra no débito. */
  debito: boolean
  description: string
  amount: string
  type: TransactionType
  status: TransactionStatus
  categoriaId: string
  date: string
  notes: string
  anexoUrl: string
  tagIds: string[]
  ajusteDeRecorrenciaId: string
}

function emptyForm(accountId = ''): FormState {
  return {
    accountId, destinationAccountId: '', meioPagamento: 'conta', partes: partesVazias(), divisaoId: '', parcelas: '1', cartaoId: '', debito: false, description: '', amount: '',
    type: TransactionType.Expense, status: TransactionStatus.Confirmed,
    categoriaId: '', date: new Date().toISOString().slice(0, 10),
    notes: '', anexoUrl: '', tagIds: [], ajusteDeRecorrenciaId: '',
  }
}

export function TransacoesPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [pagina, setPagina] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FinancialTransaction | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [erro, setErro] = useState<string | null>(null)
  const [paraExcluir, setParaExcluir] = useState<FinancialTransaction | null>(null)
  const [mostrarNovaCategoria, setMostrarNovaCategoria] = useState(false)
  const [novaCategoria, setNovaCategoria] = useState('')
  const [historicoOpen, setHistoricoOpen] = useState(false)
  const [baixaAlvo, setBaixaAlvo] = useState<FinancialTransaction | null>(null)
  // Fatura paga: o dinheiro já saiu da conta, então só a categoria muda (e descrição/observação, se veio da Pluggy).
  const soCategoria = !!editing?.faturaPaga
  const textoLivre = soCategoria && !!editing?.ehDaPluggy
  const [dataBaixa, setDataBaixa] = useState(new Date().toISOString().slice(0, 10))
  const [verLiquido, setVerLiquido] = useState<FinancialTransaction | null>(null)
  const [escopoSalvar, setEscopoSalvar] = useState(false)
  const [escopoEscolhido, setEscopoEscolhido] = useState<EscopoParcelas>(EscopoParcelas.Apenas)

  // Chegou do Dashboard (toque num card ou no aviso de atrasados): aplica o filtro que o card representa.
  const [busca] = useSearchParams()
  const tipoParam = busca.get('tipo')
  const [situacao, setSituacao] = useState<Situacao>((busca.get('situacao') as Situacao) ?? 'todas')
  const [periodo, setPeriodo] = useState<{ mes: number; ano: number } | null>(
    busca.get('mes') && busca.get('ano') ? { mes: Number(busca.get('mes')), ano: Number(busca.get('ano')) } : null,
  )
  const [caixa, setCaixa] = useState(busca.get('caixa') === '1')
  const [filtro, setFiltro] = useState<number>(
    tipoParam === 'receitas' ? TransactionType.Income : tipoParam === 'despesas' ? TransactionType.Expense : TODOS,
  )

  // Filtros extras: conta, cartão, categoria e um período livre (substitui o navegador de mês quando usado).
  const [contaFiltro, setContaFiltro] = useState('')
  const [cartaoFiltro, setCartaoFiltro] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState(busca.get('semCategoria') === '1' ? SEM_CATEGORIA : busca.get('categoriaId') ?? '')
  const [periodoLivre, setPeriodoLivre] = useState<{ de: string; ate: string } | null>(null)
  const [buscaTexto, setBuscaTexto] = useState('')
  const [ordenarPor, setOrdenarPor] = useState<'data' | 'nome' | 'parcela' | 'valor'>('data')
  const [ordemDesc, setOrdemDesc] = useState(true)
  function alternarOrdenacao(campo: typeof ordenarPor) {
    setOrdemDesc((atual) => (campo === ordenarPor ? !atual : true))
    setOrdenarPor(campo)
    setPagina(1)
  }

  const atrasadas = situacao === 'atrasadas'
  const hoje = new Date()
  const mesAtual = !!periodo && periodo.mes === hoje.getMonth() + 1 && periodo.ano === hoje.getFullYear()

  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['transacoes', filtro, pagina, situacao, periodo, caixa, contaFiltro, cartaoFiltro, categoriaFiltro, periodoLivre, buscaTexto, ordenarPor, ordemDesc],
    queryFn: () => transacoesApi.list({
      type: filtro === TODOS ? undefined : (filtro as TransactionType),
      status: situacao === 'pendentes' ? TransactionStatus.Pending : situacao === 'confirmadas' ? TransactionStatus.Confirmed : undefined,
      atrasadas: atrasadas || undefined,
      excluirCartao: caixa || atrasadas || undefined,
      accountId: contaFiltro || undefined,
      cartaoId: cartaoFiltro || undefined,
      categoriaId: categoriaFiltro && categoriaFiltro !== SEM_CATEGORIA ? categoriaFiltro : undefined,
      semCategoria: categoriaFiltro === SEM_CATEGORIA || undefined,
      busca: buscaTexto.trim() || undefined,
      ordenarPor,
      ordemDesc,
      // Um período livre manda mais que o navegador de mês.
      dataInicio: periodoLivre
        ? new Date(`${periodoLivre.de}T00:00:00Z`).toISOString()
        : atrasadas || !periodo
          ? undefined
          : situacao === 'pendentes' && mesAtual
            ? new Date(Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())).toISOString()
            : new Date(Date.UTC(periodo.ano, periodo.mes - 1, 1)).toISOString(),
      dataFim: periodoLivre
        ? new Date(`${periodoLivre.ate}T23:59:59Z`).toISOString()
        : atrasadas || !periodo ? undefined : new Date(Date.UTC(periodo.ano, periodo.mes, 0, 23, 59, 59)).toISOString(),
      pagina,
      itensPorPagina: ITENS_POR_PAGINA,
    }),
  })

  // Fatura de cartão aparece como um lançamento na data de vencimento (despesa; pendente até ser paga).
  const { data: faturasTodas = [] } = useQuery({
    queryKey: ['faturas-resumo', periodo, situacao],
    queryFn: () => cartoesApi.faturasResumo(atrasadas ? { apenasAtrasadas: true } : periodo ? { mes: periodo.mes, ano: periodo.ano } : { apenasAbertas: true }),
    enabled: filtro !== TransactionType.Income,
  })
  const faturas = faturasTodas.filter((x: FaturaResumo) => {
    if (filtro !== TODOS && filtro !== TransactionType.Expense) return false
    const aberta = x.status !== StatusFatura.Paga
    if (situacao === 'confirmadas') return !aberta
    if (situacao === 'atrasadas') return aberta && x.atrasada
    if (situacao === 'pendentes') return aberta && !x.atrasada
    return true
  })

  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list() })

  // Extrato de uma conta com corte: linha de saldo inicial e histórico (anterior ao corte) esmaecido.
  const contaComCorte = contas.find((c) => c.id === contaFiltro && c.dataAbertura)
  const corte = contaComCorte?.dataAbertura?.slice(0, 10)
  const ehHistorico = (t: FinancialTransaction) =>
    !!corte && !t.cartaoId && t.date.slice(0, 10) < corte &&
    (t.accountId === contaFiltro || t.destinationAccountId === contaFiltro)
  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
  })
  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list })
  const dividindo = form.meioPagamento === 'dividido'
  const { data: cartoesUtilizaveis = [] } = useQuery({ queryKey: ['cartoes-utilizaveis'], queryFn: comprasDivididasApi.cartoesUtilizaveis, enabled: modalOpen && dividindo })
  const ownId = useAuthStore((st) => st.userId)
  const principalId = useContextoStore((st) => st.principalId)
  // Só o crédito parcela e cai em fatura; débito e pré-pago saem da conta (ou do saldo do vale) à vista.
  const cartaoEscolhido = cartoes.find((c) => c.id === form.cartaoId)
  const cartaoComDebito = !!cartaoEscolhido && cartaoEscolhido.modalidade === ModalidadeCartao.Credito && cartaoEscolhido.temDebito
  const cartaoAVista = !!cartaoEscolhido && (cartaoEscolhido.modalidade !== ModalidadeCartao.Credito || (cartaoComDebito && form.debito))
  const { data: allTags = [] } = useQuery({ queryKey: ['tags'], queryFn: categoriasApi.listTags, enabled: modalOpen })
  const { data: receitasRecorrentes = [] } = useQuery({
    queryKey: ['recorrencias', 'receitas-ativas'],
    queryFn: () => recorrenciasApi.list({ apenasAtivas: true }),
    select: (p) => p.itens.filter((r) => r.type === TransactionType.Income),
    enabled: modalOpen,
  })
  const { data: historico = [], isLoading: historicoLoading } = useQuery({
    queryKey: ['transacoes-historico'],
    queryFn: () => transacoesApi.historico(),
    enabled: historicoOpen,
  })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
    qc.invalidateQueries({ queryKey: ['orcamento'] })
    qc.invalidateQueries({ queryKey: ['contas'] })
    qc.invalidateQueries({ queryKey: ['cartoes'] })
    qc.invalidateQueries({ queryKey: ['fatura'] })
    qc.invalidateQueries({ queryKey: ['transacoes-historico'] })
    qc.invalidateQueries({ queryKey: ['faturas-resumo'] })
    qc.invalidateQueries({ queryKey: ['cartoes-utilizaveis'] })
  }

  const salvar = useMutation<unknown, Error, EscopoParcelas | undefined>({
    mutationFn: async (escopo) => {
      if (form.meioPagamento === 'dividido') {
        const req = montarRequest({
          description: form.description, valorTotal: Number(form.amount), date: form.date,
          categoriaId: form.categoriaId || undefined, notes: form.notes || undefined,
          contaCompartilhadaOwnerId: principalId && principalId !== ownId ? principalId : null,
        }, form.partes, cartoesUtilizaveis)
        return form.divisaoId ? comprasDivididasApi.atualizar(form.divisaoId, req) : comprasDivididasApi.criar(req)
      }
      const payload = {
        description: form.description,
        amount: Number(form.amount),
        type: form.type,
        status: form.status,
        // Sempre manda as duas contas reais (origem/destino) — a API decide sozinha qual perna é qual,
        // então dá pra editar as duas de qualquer uma das pernas.
        accountId: form.type === TransactionType.Transfer ? form.accountId : undefined,
        destinationAccountId: form.type === TransactionType.Transfer ? form.destinationAccountId : undefined,
        categoriaId: form.categoriaId || undefined,
        date: form.date,
        notes: form.notes || undefined,
        anexoUrl: form.anexoUrl || undefined,
      }
      const ajustePayload = form.type !== TransactionType.Transfer && form.meioPagamento === 'conta'
        ? form.ajusteDeRecorrenciaId
          ? { ajusteDeRecorrenciaId: form.ajusteDeRecorrenciaId }
          : editing?.ajusteDeRecorrenciaId ? { removerAjusteDeRecorrencia: true } : {}
        : {}
      if (editing && soCategoria) {
        return transacoesApi.atualizar(editing.id, {
          categoriaId: payload.categoriaId,
          ...(textoLivre ? { description: payload.description, notes: payload.notes } : {}),
        }, escopo ?? EscopoParcelas.Apenas)
      }
      if (editing) {
        try {
          return await transacoesApi.atualizar(editing.id, { ...payload, ...ajustePayload, tagIds: form.tagIds }, escopo ?? EscopoParcelas.Apenas)
        } catch (e) {
          // Fatura já paga: só descrição/categoria/observação de um lançamento da Pluggy podem mudar depois.
          if (editing.ehDaPluggy) {
            return await transacoesApi.atualizar(editing.id, {
              description: payload.description, categoriaId: payload.categoriaId, notes: payload.notes,
            }, escopo ?? EscopoParcelas.Apenas)
          }
          throw e
        }
      }
      if (form.meioPagamento === 'cartao') {
        return cartoesApi.adicionarTransacao(form.cartaoId, {
          description: form.description,
          amount: Number(form.amount),
          date: form.date,
          numParcelas: cartaoAVista ? 1 : Math.min(24, Math.max(1, Math.floor(Number(form.parcelas)) || 1)),
          categoriaId: form.categoriaId || undefined,
          notes: form.notes || undefined,
          debito: cartaoComDebito && form.debito ? true : undefined,
        })
      }
      return transacoesApi.criar({ ...payload, ...ajustePayload, accountId: form.accountId, tagIds: form.tagIds.length ? form.tagIds : undefined })
    },
    onSuccess: (r) => {
      invalidate()
      if (r && typeof r === 'object' && 'divisaoId' in r) {
        const d = r as CompraDividida
        toast.success(`Compra dividida em ${d.partes.length} cartões.${d.avisos.length ? ' Atenção: ' + d.avisos.join(' ') : ''}`)
        setEscopoSalvar(false)
        closeModal()
        return
      }
      const compra = r && typeof r === 'object' && 'parcelas' in r ? (r as CompraCartaoResultado) : null
      toast.success(compra && compra.parcelas.length > 0 ? resumoDaCompra(compra) : compra ? 'Compra registrada.' : editing ? 'Lançamento atualizado.' : 'Lançamento criado.')
      setEscopoSalvar(false)
      closeModal()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const criarCategoria = useMutation({
    mutationFn: () => categoriasApi.criar({ nome: novaCategoria.trim() }),
    onSuccess: (categoria) => {
      qc.invalidateQueries({ queryKey: ['categorias'] })
      setForm((f) => ({ ...f, categoriaId: categoria.id }))
      setNovaCategoria('')
      setMostrarNovaCategoria(false)
      toast.success('Categoria criada e selecionada.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const darBaixa = useMutation({
    mutationFn: (p: { t: FinancialTransaction; dataBaixa: string }) => transacoesApi.atualizar(p.t.id, { status: TransactionStatus.Confirmed, dataBaixa: p.dataBaixa }),
    onSuccess: () => {
      setBaixaAlvo(null)
      invalidate()
      toast.success('Lançamento confirmado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: ({ id, escopo, divisaoId }: { id: string; escopo: EscopoParcelas; divisaoId?: string }) =>
      divisaoId ? comprasDivididasApi.remover(divisaoId) : transacoesApi.remover(id, escopo),
    onSuccess: () => {
      invalidate()
      toast.success(paraExcluir?.divisaoCompraId ? 'Compra dividida excluída (todas as partes).' : 'Lançamento excluído.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    setErro(null)
    setMostrarNovaCategoria(false)
    setNovaCategoria('')
  }

  function openNew() {
    setEditing(null)
    setErro(null)
    setForm(emptyForm(contas[0]?.id ?? ''))
    setModalOpen(true)
  }

  // Vindo do detalhe do Comparativo (Relatórios): abre direto a edição do lançamento.
  const editarId = busca.get('editar')
  useEffect(() => {
    if (!editarId) return
    let vivo = true
    transacoesApi.get(editarId).then((t) => { if (vivo) openEdit(t) }).catch(() => undefined)
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editarId])

  function openEdit(t: FinancialTransaction) {
    setEditing(t)
    setErro(null)
    // Uma transferência é duas linhas (saída/entrada); esta pode ser qualquer uma das duas. Mostra sempre
    // "de onde saiu → pra onde foi" de verdade (senão a perna de entrada aparece com origem/destino trocados).
    const ehEntrada = t.type === TransactionType.Transfer && t.direcao === 2
    setForm({
      accountId: ehEntrada ? (t.destinationAccountId ?? '') : t.accountId,
      destinationAccountId: ehEntrada ? t.accountId : (t.destinationAccountId ?? ''),
      meioPagamento: t.cartaoId ? 'cartao' : 'conta',
      partes: partesVazias(),
      divisaoId: '',
      parcelas: '1',
      cartaoId: t.cartaoId ?? '',
      debito: false,
      description: t.description,
      amount: String(Math.abs(t.amount)),
      type: t.type,
      status: t.status,
      categoriaId: t.categoriaId ?? '',
      date: t.date.slice(0, 10),
      notes: t.notes ?? '',
      anexoUrl: t.anexoUrl ?? '',
      tagIds: t.tags.map((tg) => tg.id),
      ajusteDeRecorrenciaId: t.ajusteDeRecorrenciaId ?? '',
    })
    setModalOpen(true)
    if (t.divisaoCompraId) {
      // Compra dividida: edita a compra inteira, não só esta parte.
      comprasDivididasApi.obter(t.divisaoCompraId).then((d) => {
        setForm((f) => ({
          ...f, meioPagamento: 'dividido', divisaoId: d.divisaoId, partes: partesDaCompra(d),
          description: d.description, amount: String(d.valorTotal), date: d.date.slice(0, 10), status: TransactionStatus.Confirmed,
        }))
      }).catch((e: Error) => setErro(e.message))
    }
  }

  function toggleTag(id: string) {
    setForm((f) => ({ ...f, tagIds: f.tagIds.includes(id) ? f.tagIds.filter((x) => x !== id) : [...f.tagIds, id] }))
  }

  function submit() {
    if (form.type === TransactionType.Transfer) {
      if (!form.accountId || !form.destinationAccountId || form.accountId === form.destinationAccountId || !form.description.trim() || !form.amount) {
        setErro('Preencha motivo, conta de origem, conta de destino e valor. As contas devem ser diferentes.')
        return
      }
    }
    if (form.meioPagamento === 'dividido') {
      const falha = !form.description.trim() ? 'Informe a descrição.' : validarPartes(Number(form.amount), form.partes)
      if (falha) { setErro(falha); return }
      salvar.mutate(EscopoParcelas.Apenas)
      return
    }
    const origemValida = form.meioPagamento === 'cartao' ? !!form.cartaoId : !!form.accountId
    if (!origemValida || !form.description.trim() || !form.amount) {
      setErro(`Preencha ${form.meioPagamento === 'cartao' ? 'cartão' : 'conta'}, descrição e valor.`)
      return
    }
    // Compra parcelada: pergunta quais parcelas a alteração atinge.
    if (editing && editing.compraId && (editing.parcelaTotalNumero ?? 0) > 1) {
      setEscopoEscolhido(EscopoParcelas.Apenas)
      setEscopoSalvar(true)
      return
    }
    salvar.mutate(EscopoParcelas.Apenas)
  }

  const parcelada = (t: FinancialTransaction | null) => !t?.divisaoCompraId && !!t?.compraId && (t.parcelaTotalNumero ?? 0) > 1

  const transacoes = page?.itens ?? []
  const totalPaginas = page?.totalPaginas || 1
  const total = page?.totalRegistros ?? 0

  return (
    <>
      <PageHeader title="Transações" />

      <Card>
        <CardHeader
          title="Histórico de lançamentos"
          actions={
            <>
              <Link to="/notas-fiscais" className="inline-flex h-[38px] items-center gap-2 rounded bg-info/10 px-4 text-13 font-medium text-info hover:bg-info hover:text-white">
                <i className="ri-qr-scan-2-line text-base" aria-hidden /> Lançar por nota
              </Link>
              <Button variant="light" icon="ri-history-line" onClick={() => setHistoricoOpen(true)}>Histórico</Button>
              <Button data-tutorial="lancamento.novo" icon="ri-add-line" onClick={openNew}>Novo lançamento</Button>
            </>
          }
        />
        <Tabs tabs={TABS} active={filtro} onChange={(k) => { setFiltro(k); setPagina(1) }} className="px-4" />

        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-4 py-3">
          <div className="flex flex-wrap gap-1.5">
            {([['todas', 'Qualquer situação'], ['pendentes', 'Pendentes'], ['atrasadas', 'Atrasadas'], ['confirmadas', 'Realizadas']] as [Situacao, string][]).map(([k, rotulo]) => (
              <button
                key={k} type="button" aria-pressed={situacao === k}
                onClick={() => { setSituacao(k); setPagina(1) }}
                className={cn('rounded border px-3 py-1 text-13 transition-colors', situacao === k ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white')}
              >{rotulo}</button>
            ))}
          </div>
          {!atrasadas && !periodoLivre && (periodo ? (
            <div className="flex items-center gap-2">
              <MonthNavigator mes={periodo.mes} ano={periodo.ano} onChange={(m, a) => { setPeriodo({ mes: m, ano: a }); setPagina(1) }} />
              <button type="button" className="text-13 text-muted hover:text-white" onClick={() => { setPeriodo(null); setPagina(1) }}>Todos os meses</button>
            </div>
          ) : (
            <button type="button" className="text-13 text-primary hover:underline" onClick={() => { setPeriodo({ mes: hoje.getMonth() + 1, ano: hoje.getFullYear() }); setPagina(1) }}>
              <i className="ri-calendar-2-line" aria-hidden /> Filtrar por mês
            </button>
          ))}
          {atrasadas && <span className="text-13 text-muted">Pendentes com data anterior a hoje, de qualquer mês.</span>}
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-border/60 px-4 py-3">
          <div className="w-52">
            <Input label="Buscar por nome" placeholder="Descrição do lançamento" value={buscaTexto}
              onChange={(e) => { setBuscaTexto(e.target.value); setPagina(1) }} />
          </div>
          <button
            type="button"
            className={cn('mb-2.5 inline-flex items-center gap-1 rounded border px-3 py-2 text-13',
              ordenarPor === 'parcela' ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white')}
            onClick={() => alternarOrdenacao('parcela')}
          >
            Ordenar por parcela
            <i className={cn('text-2xs', ordenarPor === 'parcela' ? (ordemDesc ? 'ri-arrow-down-line' : 'ri-arrow-up-line') : 'ri-expand-up-down-line opacity-40')} aria-hidden />
          </button>
          <div className="w-40">
            <Select label="Conta" value={contaFiltro} onChange={(e) => { setContaFiltro(e.target.value); setPagina(1) }}>
              <option value="">Todas</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="w-40">
            <Select
              label="Cartão" value={caixa ? SEM_CARTAO : cartaoFiltro}
              onChange={(e) => {
                if (e.target.value === SEM_CARTAO) { setCaixa(true); setCartaoFiltro('') }
                else { setCaixa(false); setCartaoFiltro(e.target.value) }
                setPagina(1)
              }}
            >
              <option value="">Todos</option>
              <option value={SEM_CARTAO}>Sem cartão (só conta)</option>
              {cartoes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </div>
          <div className="w-44">
            <Select label="Categoria" value={categoriaFiltro} onChange={(e) => { setCategoriaFiltro(e.target.value); setPagina(1) }}>
              <option value="">Todas</option>
              <option value={SEM_CATEGORIA}>Sem categoria</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Input label="De" type="date" value={periodoLivre?.de ?? ''}
              onChange={(e) => { setPeriodoLivre((p) => ({ de: e.target.value, ate: p?.ate ?? e.target.value })); setPagina(1) }} />
            <Input label="Até" type="date" value={periodoLivre?.ate ?? ''}
              onChange={(e) => { setPeriodoLivre((p) => ({ de: p?.de ?? e.target.value, ate: e.target.value })); setPagina(1) }} />
            {periodoLivre && (
              <button type="button" className="mb-2.5 text-13 text-muted hover:text-white" onClick={() => { setPeriodoLivre(null); setPagina(1) }}>
                <i className="ri-close-line" aria-hidden /> Limpar período
              </button>
            )}
          </div>
          {(contaFiltro || cartaoFiltro || categoriaFiltro || caixa) && (
            <button type="button" className="mb-2.5 text-13 text-muted hover:text-white"
              onClick={() => { setContaFiltro(''); setCartaoFiltro(''); setCategoriaFiltro(''); setCaixa(false); setPagina(1) }}>
              <i className="ri-filter-off-line" aria-hidden /> Limpar filtros
            </button>
          )}
        </div>

        {faturas.length > 0 && (
          <div className="border-b border-border/60 px-4 py-3">
            <p className="mb-2 text-13 font-semibold text-white"><i className="ri-bank-card-line text-info" aria-hidden /> Faturas de cartão</p>
            <div className="space-y-2">
              {faturas.map((x) => {
                const paga = x.status === StatusFatura.Paga
                return (
                  <div key={x.id} className={cn('flex flex-wrap items-center justify-between gap-3 rounded border px-3 py-2', x.atrasada ? 'border-expense/50 bg-expense/5' : 'border-border/60 bg-surface2/40')}>
                    <div className="min-w-0">
                      <p className="font-medium text-white">Fatura {x.cartaoNome}</p>
                      <p className="text-2xs text-muted">{paga ? `paga em ${formatDate(x.dataPagamento!)}` : `vence ${formatDate(x.dataVencimento)}`} · sai de {x.accountName}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={paga ? 'income' : x.atrasada ? 'expense' : 'warning'}>{paga ? (x.pagaSemDebito ? 'Paga sem débito' : 'Paga') : x.atrasada ? 'Atrasada' : 'Pendente'}</Badge>
                      <span className="font-semibold text-expense">-{formatMoney(x.valorTotal)}</span>
                      {!paga && <Link to={`/cartoes?cartao=${x.cartaoId}&mes=${x.mes}&ano=${x.ano}&pagar=1`} className="inline-flex h-8 items-center rounded bg-income/10 px-3 text-13 font-medium text-income hover:bg-income hover:text-white">Pagar</Link>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {contaComCorte && (
          <div className="flex items-center justify-between gap-3 border-b border-border/70 bg-surface2 px-4 py-2.5">
            <span className="text-13 text-white">Saldo inicial em {diaMes(corte!)}</span>
            <b className="text-sm text-white">{formatMoney(contaComCorte.saldoAbertura ?? 0)}</b>
          </div>
        )}

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message="Erro ao carregar transações." />
        ) : transacoes.length === 0 ? (
          <EmptyState icon="ri-exchange-dollar-line" message="Nenhuma transação encontrada." action={<Button variant="soft-primary" icon="ri-add-line" onClick={openNew}>Novo lançamento</Button>} />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <ThOrdenavel campo="nome" ativo={ordenarPor} desc={ordemDesc} onClick={alternarOrdenacao}>Descrição</ThOrdenavel>
                  <Th>Categoria</Th>
                  <ThOrdenavel campo="data" ativo={ordenarPor} desc={ordemDesc} onClick={alternarOrdenacao}>Data</ThOrdenavel>
                  <Th>Status</Th>
                  <ThOrdenavel campo="valor" ativo={ordenarPor} desc={ordemDesc} onClick={alternarOrdenacao} className="text-right">Valor</ThOrdenavel>
                  <Th className="text-right">Ações</Th>
                </tr>
              </THead>
              <tbody>
                {transacoes.map((t, idx) => {
                  const visualBase = TYPE_VISUAL[t.type] ?? TYPE_VISUAL[TransactionType.Expense]
                  // Débito que paga uma fatura (total ou parcial): liquidação neutra, sem cara de despesa comum.
                  const liquidaFatura = !!t.liquidaFaturaId
                  const visual = liquidaFatura ? { icon: 'ri-bank-card-line', tone: 'muted' as const, text: 'text-muted', sign: visualBase.sign } : visualBase
                  const historico = ehHistorico(t)
                  // Com a lista por data, o separador marca o ponto exato do corte; em outras ordens só esmaece cada linha.
                  const separador = historico && ordenarPor === 'data' && (idx === 0 || !ehHistorico(transacoes[idx - 1]))
                  return (
                    <Fragment key={t.id}>
                    {separador && (
                      <tr>
                        <td colSpan={6} className="border-b border-border/60 bg-bg/40 px-4 py-2 text-center text-2xs tracking-wider text-subtle">
                          HISTÓRICO · ANTES DO CORTE, NÃO MEXE NO SALDO
                        </td>
                      </tr>
                    )}
                    <Tr className={historico ? 'opacity-60' : undefined}>
                      <Td>
                        <div className="flex items-center gap-3">
                          <AvatarIcon icon={visual.icon} tone={visual.tone} size="sm" />
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 font-medium text-white">
                              <span className="truncate">{t.description}</span>
                              {t.notaFiscalId && <Link to={`/notas-fiscais?nota=${t.notaFiscalId}`} title="Ver nota fiscal (itens da compra)" className="text-primary"><i className="ri-receipt-line" aria-hidden /></Link>}
                              {t.parcelaNumero && <Badge variant="muted">{t.parcelaNumero}/{t.parcelaTotalNumero}</Badge>}
                              {liquidaFatura && <span title="Não conta como gasto: as compras da fatura já foram o consumo."><Badge variant="muted">Liquida fatura</Badge></span>}
                              {liquidaFatura && t.liquidacaoParcial && <span title="Não conta como gasto: as compras da fatura já foram o consumo."><Badge variant="muted">Pagamento parcial</Badge></span>}
                              {t.divisaoCompraId && <Badge variant="info">Dividida</Badge>}
                              <DonoBadge nome={t.ownerNome} />
                              {t.recurrenceId && t.type === TransactionType.Income && (
                                <button
                                  type="button" title="Ver líquido esperado"
                                  className="text-info hover:text-white"
                                  onClick={() => setVerLiquido(t)}
                                >
                                  <i className="ri-information-line" aria-hidden />
                                </button>
                              )}
                            </p>
                            <p className="truncate text-2xs text-muted">
                              {t.type === TransactionType.Transfer && t.destinationAccountName
                                ? t.direcao === 2
                                  ? `${t.destinationAccountName} → ${t.accountName}`
                                  : `${t.accountName} → ${t.destinationAccountName}`
                                : t.accountName}
                              {t.vinculoReceitaDescricao && ` · desconto/ajuste de ${t.vinculoReceitaDescricao}`}
                            </p>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <p className="text-white">{t.categoriaNome ? `${t.categoriaIcone ?? ''} ${t.categoriaNome}` : <span className="text-muted">—</span>}</p>
                        {t.tags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {t.tags.map((tg) => (
                              <span key={tg.id} className="rounded px-1.5 text-2xs" style={{ background: `${tg.cor ?? '#8B949E'}22`, color: tg.cor ?? '#8B949E' }}>{tg.nome}</span>
                            ))}
                          </div>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap text-muted">{formatDate(t.date)}{t.dataBaixa && t.dataBaixa.slice(0, 10) !== t.date.slice(0, 10) && <span className="block text-2xs">baixa {formatDate(t.dataBaixa)}</span>}</Td>
                      <Td><Badge variant={STATUS_BADGE[t.status] ?? 'muted'}>{TRANSACTION_STATUS_LABEL[t.status]}</Badge></Td>
                      <Td className={cn('whitespace-nowrap text-right font-semibold', visual.text)}>{visual.sign}{formatMoney(Math.abs(t.amount))}</Td>
                      <Td className="whitespace-nowrap text-right">
                        {t.status === TransactionStatus.Pending && !t.cartaoId && (
                          <IconButton
                            icon="ri-check-double-line"
                            label={`Dar baixa em ${t.description}`}
                            tone="success"
                            disabled={darBaixa.isPending}
                            onClick={() => { setDataBaixa(new Date().toISOString().slice(0, 10)); setBaixaAlvo(t) }}
                          />
                        )}
                        <IconButton icon="ri-pencil-fill" label={`Editar ${t.description}`} tone="info" onClick={() => openEdit(t)} />
                        <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${t.description}`} tone="danger" onClick={() => setParaExcluir(t)} />
                      </Td>
                    </Tr>
                    </Fragment>
                  )
                })}
              </tbody>
            </Table>
            {contaComCorte && ordenarPor !== 'data' && transacoes.some(ehHistorico) && (
              <p className="border-t border-border/60 px-4 py-2 text-2xs text-subtle">Linhas esmaecidas: histórico anterior ao corte, não mexe no saldo.</p>
            )}
            <Pagination
              page={pagina}
              totalPages={totalPaginas}
              onChange={setPagina}
              summary={`Mostrando ${transacoes.length} de ${total} lançamento${total !== 1 ? 's' : ''}`}
            />
          </>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? 'Editar lançamento' : 'Novo lançamento'}
        size="lg"
        footer={
          <>
            <Button variant="light" onClick={closeModal}>Cancelar</Button>
            <Button icon={editing ? 'ri-save-line' : 'ri-add-line'} onClick={submit} loading={salvar.isPending}>
              {editing ? 'Salvar alterações' : 'Criar lançamento'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <span className="form-label">Tipo</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {FORM_TYPES.map((tp) => (
                <button
                  key={tp}
                  type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    type: tp,
                    meioPagamento: tp === TransactionType.Expense ? f.meioPagamento : 'conta',
                  }))}
                  aria-pressed={form.type === tp}
                  className={cn(
                    'flex h-[38px] items-center justify-center gap-1.5 rounded border text-13 transition-colors',
                    form.type === tp ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white',
                  )}
                >
                  <i className={TYPE_VISUAL[tp].icon} aria-hidden /> {TRANSACTION_TYPE_LABEL[tp]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {soCategoria && <p className="text-13 text-warning sm:col-span-2">Fatura paga: só a categoria pode ser alterada.</p>}
            <Input
              label={form.type === TransactionType.Transfer ? 'Motivo' : 'Descrição'}
              disabled={soCategoria && !textoLivre}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={form.type === TransactionType.Transfer ? 'Ex: Reserva para investimentos' : 'Ex: Aluguel'}
            />
            {form.type === TransactionType.Expense && (
              <Select
                data-tutorial="lancamento.pagamento"
                label="Forma de pagamento"
                value={form.meioPagamento}
                disabled={!!editing}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  meioPagamento: e.target.value as 'conta' | 'cartao' | 'dividido',
                  status: e.target.value === 'cartao' ? TransactionStatus.Pending : f.status,
                }))}
              >
                <option value="conta">Conta</option>
                <option value="cartao">Cartão (crédito, débito ou pré-pago)</option>
                <option value="dividido">Dividir em cartões (até 2)</option>
              </Select>
            )}
            {form.meioPagamento === 'cartao' && form.type === TransactionType.Expense ? (
              <Select label="Cartão" value={form.cartaoId} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, cartaoId: e.target.value, debito: false }))}>
                <option value="">Selecione...</option>
                {cartoes.map((c) => <option key={c.id} value={c.id}>{c.nome} · {MODALIDADE_CARTAO_LABEL[c.modalidade]}{c.modalidade === ModalidadeCartao.Debito ? ` · ${c.accountName}` : ''}</option>)}
              </Select>
            ) : dividindo && form.type === TransactionType.Expense ? null : (
              <Select label={form.type === TransactionType.Transfer ? 'Conta de origem' : 'Conta'} value={form.accountId} disabled={!!editing && form.type !== TransactionType.Transfer} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}>
                <option value="">Selecione...</option>
                {contas.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
              </Select>
            )}
            {form.type === TransactionType.Transfer && (
              <Select label="Conta de destino" value={form.destinationAccountId} onChange={(e) => setForm((f) => ({ ...f, destinationAccountId: e.target.value }))}>
                <option value="">Selecione...</option>
                {contas.filter((c) => c.id !== form.accountId).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
              </Select>
            )}
            {form.meioPagamento === 'cartao' && form.type === TransactionType.Expense && !editing && cartaoComDebito && (
              <Select label="Como pagou" value={form.debito ? 'debito' : 'credito'} onChange={(e) => setForm((f) => ({ ...f, debito: e.target.value === 'debito' }))}>
                <option value="credito">Crédito (entra na fatura)</option>
                <option value="debito">Débito (sai da conta na hora)</option>
              </Select>
            )}
            <Input disabled={soCategoria} label={(form.meioPagamento === 'cartao' && !editing && !cartaoAVista) || dividindo ? 'Valor total (R$)' : 'Valor (R$)'} type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0,00" />
            {form.meioPagamento === 'cartao' && form.type === TransactionType.Expense && !editing && cartaoAVista && (
              <p className="self-end pb-2 text-13 text-muted">À vista: sai {cartaoEscolhido?.modalidade === ModalidadeCartao.PrePago ? 'do saldo do cartão' : `da conta ${cartaoEscolhido?.accountName}`} na hora.</p>
            )}
            {form.meioPagamento === 'cartao' && form.type === TransactionType.Expense && !editing && !cartaoAVista && (
              <div>
                <Input label="Parcelas (1 a 24)" type="number" min={1} max={24} value={form.parcelas} onChange={(e) => setForm((f) => ({ ...f, parcelas: e.target.value }))} />
                {Number(form.amount) > 0 && Number(form.parcelas) > 1 && (
                  <p className="mt-1 text-2xs text-muted">{Math.floor(Number(form.parcelas))}x de {formatMoney(Number(form.amount) / Math.floor(Number(form.parcelas)))}</p>
                )}
              </div>
            )}
            {dividindo && form.type === TransactionType.Expense && (
              <div data-tutorial="lancamento.dividir" className="sm:col-span-2">
                <PartesDivisao valorTotal={Number(form.amount) || 0} partes={form.partes} cartoes={cartoesUtilizaveis} onChange={(partes) => setForm((f) => ({ ...f, partes }))} />
              </div>
            )}
            <Input label="Data" type="date" disabled={soCategoria} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            <Select label="Status" value={form.meioPagamento === 'cartao' && !editing ? (cartaoAVista ? TransactionStatus.Confirmed : TransactionStatus.Pending) : form.status} disabled={form.meioPagamento !== 'conta' || soCategoria} onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) as TransactionStatus }))}>
              {FORM_STATUSES.map((s) => <option key={s} value={s}>{TRANSACTION_STATUS_LABEL[s]}</option>)}
            </Select>
            {form.type !== TransactionType.Transfer && <div>
              <Select label="Categoria" value={form.categoriaId} onChange={(e) => setForm((f) => ({ ...f, categoriaId: e.target.value }))}>
                <option value="">Sem categoria</option>
                {categorias.map((c) => (
                  <optgroup key={c.id} label={`${c.icone ?? ''} ${c.nome}`}>
                    <option value={c.id}>{c.icone} {c.nome}</option>
                    {c.subCategorias.map((s) => <option key={s.id} value={s.id}>↳ {s.nome}</option>)}
                  </optgroup>
                ))}
              </Select>
              {!mostrarNovaCategoria ? (
                <button type="button" className="mt-1.5 text-xs text-primary hover:underline" onClick={() => setMostrarNovaCategoria(true)}>
                  <i className="ri-add-line" aria-hidden /> Cadastrar nova categoria
                </button>
              ) : (
                <div className="mt-2 flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <Input value={novaCategoria} onChange={(e) => setNovaCategoria(e.target.value)} placeholder="Nome da categoria" autoFocus />
                  </div>
                  <Button size="sm" onClick={() => novaCategoria.trim() && criarCategoria.mutate()} loading={criarCategoria.isPending}>Criar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setMostrarNovaCategoria(false)}>Cancelar</Button>
                </div>
              )}
            </div>}
          </div>

          {form.type !== TransactionType.Transfer && form.meioPagamento === 'conta' && !editing?.recurrenceId && receitasRecorrentes.length > 0 && (
            <div>
              <Select
                label="Ajuste de receita recorrente (opcional)"
                value={form.ajusteDeRecorrenciaId}
                onChange={(e) => setForm((f) => ({ ...f, ajusteDeRecorrenciaId: e.target.value }))}
              >
                <option value="">Nenhum — lançamento independente</option>
                {receitasRecorrentes.map((r) => <option key={r.id} value={r.id}>{r.description} · {formatMoney(r.amount)}</option>)}
              </Select>
              <p className="mt-1 text-2xs text-muted">
                Marca como um ajuste pontual do líquido esperado daquela receita neste mês (ex: hora extra soma, falta desconta) —
                já veio embutido no valor que a Pluggy importa.
              </p>
            </div>
          )}

          {form.type !== TransactionType.Transfer && allTags.length > 0 && (
            <div>
              <span className="form-label">Tags</span>
              <div className="flex flex-wrap gap-1.5">
                {allTags.map((tg) => {
                  const on = form.tagIds.includes(tg.id)
                  return (
                    <button
                      key={tg.id}
                      type="button"
                      onClick={() => toggleTag(tg.id)}
                      aria-pressed={on}
                      className={cn('rounded border px-2.5 py-1 text-xs transition-colors', on ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white')}
                    >
                      {on && <i className="ri-check-line mr-1" aria-hidden />}{tg.nome}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {form.type !== TransactionType.Transfer && <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Observação" disabled={soCategoria && !textoLivre} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
            <Input label="URL do comprovante" icon="ri-attachment-2" value={form.anexoUrl} onChange={(e) => setForm((f) => ({ ...f, anexoUrl: e.target.value }))} placeholder="https://..." />
          </div>}

          {editing?.ehDaPluggy && (
            <BotaoFundirLancamento transacaoId={editing.id} onFundido={() => { invalidate(); closeModal() }} />
          )}

          {editing && !editing.cartaoId && !editing.divisaoCompraId && (
            <BotaoAjusteFatura transacaoId={editing.id} onAjustado={() => { invalidate(); closeModal() }} />
          )}

          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal open={historicoOpen} onClose={() => setHistoricoOpen(false)} title="Histórico das transações" size="lg">
        {historicoLoading ? (
          <LoadingState />
        ) : historico.length === 0 ? (
          <EmptyState icon="ri-history-line" message="Nenhum evento registrado." />
        ) : (
          <Table>
            <THead>
              <tr><Th>Evento</Th><Th>Lançamento</Th><Th>Contas</Th><Th className="text-right">Valor</Th><Th>Data</Th></tr>
            </THead>
            <tbody>
              {historico.map((h) => (
                <Tr key={h.id}>
                  <Td>
                    <Badge variant={h.action === 4 || h.action === 5 ? 'expense' : h.action === 3 || h.action === 6 ? 'income' : 'info'}>
                      {TRANSACTION_HISTORY_ACTION_LABEL[h.action]}
                    </Badge>
                  </Td>
                  <Td>
                    <p className="font-medium text-white">{h.description}</p>
                    <p className="text-2xs text-muted">{TRANSACTION_TYPE_LABEL[h.type]} · {TRANSACTION_STATUS_LABEL[h.status]}</p>
                  </Td>
                  <Td className="text-muted">{h.destinationAccountName ? `${h.accountName} → ${h.destinationAccountName}` : h.accountName}</Td>
                  <Td className="whitespace-nowrap text-right font-semibold text-white">{formatMoney(h.amount)}</Td>
                  <Td className="whitespace-nowrap text-muted">{new Date(h.occurredAt).toLocaleString('pt-BR')}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir && !parcelada(paraExcluir)}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate({ id: paraExcluir.id, escopo: EscopoParcelas.Apenas, divisaoId: paraExcluir.divisaoCompraId })}
        loading={remover.isPending}
        message={<>O lançamento <strong className="text-white">{paraExcluir?.description}</strong> será excluído permanentemente.{paraExcluir?.divisaoCompraId && <> Esta compra foi dividida em cartões: todas as partes serão apagadas.</>}</>}
      />

      <Modal
        open={!!baixaAlvo}
        onClose={() => setBaixaAlvo(null)}
        title="Dar baixa"
        footer={
          <>
            <Button variant="light" onClick={() => setBaixaAlvo(null)}>Cancelar</Button>
            <Button icon="ri-check-double-line" loading={darBaixa.isPending} disabled={!dataBaixa || dataBaixa > new Date().toISOString().slice(0, 10)} onClick={() => baixaAlvo && darBaixa.mutate({ t: baixaAlvo, dataBaixa })}>Confirmar baixa</Button>
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">{baixaAlvo?.description} · {baixaAlvo ? formatMoney(Math.abs(baixaAlvo.amount)) : ""}</p>
        <Input label="Data em que foi pago / recebido" type="date" max={new Date().toISOString().slice(0, 10)} value={dataBaixa} onChange={(e) => setDataBaixa(e.target.value)} />
        <p className="mt-2 text-12 text-muted">A data prevista do lançamento não muda; a real fica guardada para conciliar com o banco.</p>
      </Modal>

      <Modal
        open={escopoSalvar || (!!paraExcluir && parcelada(paraExcluir))}
        onClose={() => { setEscopoSalvar(false); setParaExcluir(null) }}
        title={escopoSalvar ? 'Salvar alterações em qual parcela?' : 'Excluir qual parcela?'}
        footer={
          <>
            <Button variant="light" onClick={() => { setEscopoSalvar(false); setParaExcluir(null) }}>Cancelar</Button>
            {escopoSalvar ? (
              <Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate(escopoEscolhido)}>Salvar</Button>
            ) : (
              <Button variant="danger" icon="ri-delete-bin-line" loading={remover.isPending} onClick={() => paraExcluir && remover.mutate({ id: paraExcluir.id, escopo: escopoEscolhido }, { onSuccess: () => setParaExcluir(null) })}>Excluir</Button>
            )}
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">Esta compra é parcelada. Escolha o que a ação atinge:</p>
        <div className="space-y-2">
          {ESCOPOS.map((o) => (
            <label key={o.valor} className={cn('flex cursor-pointer items-start gap-3 rounded border px-3 py-2', escopoEscolhido === o.valor ? 'border-primary bg-primary/10' : 'border-border/70')}>
              <input type="radio" name="escopo" className="mt-1 accent-primary" checked={escopoEscolhido === o.valor} onChange={() => setEscopoEscolhido(o.valor)} />
              <span><span className="block text-white">{o.rotulo}</span><span className="text-2xs text-muted">{o.ajuda}</span></span>
            </label>
          ))}
        </div>
      </Modal>

      {verLiquido && <LiquidoModal transacao={verLiquido} onClose={() => setVerLiquido(null)} />}
    </>
  )
}

function LiquidoModal({ transacao, onClose }: { transacao: FinancialTransaction; onClose: () => void }) {
  const { data: liquido, isLoading } = useQuery({
    queryKey: ['recorrencias', transacao.recurrenceId, 'liquido', transacao.date],
    queryFn: () => recorrenciasApi.obterLiquido(transacao.recurrenceId!, transacao.date),
    enabled: !!transacao.recurrenceId,
  })

  return (
    <Modal open onClose={onClose} title={`Líquido esperado · ${formatDate(transacao.date)}`} size="sm">
      {isLoading || !liquido ? (
        <LoadingState />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-13">
            <span className="text-muted">Bruto</span>
            <span className="font-medium text-white">{formatMoney(liquido.bruto)}</span>
          </div>
          {liquido.itens.length === 0 ? (
            <p className="text-13 text-muted">Nenhum desconto ou ajuste vinculado a este lançamento.</p>
          ) : (
            liquido.itens.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-13">
                <span className="text-muted">{item.descricao}</span>
                <span className={item.valor < 0 ? 'text-expense' : 'text-income'}>
                  {item.valor < 0 ? '' : '+'}{formatMoney(item.valor)}
                </span>
              </div>
            ))
          )}
          <div className="flex items-center justify-between border-t border-border/60 pt-3 text-13">
            <span className="font-semibold text-white">Líquido esperado</span>
            <span className="font-semibold text-white">{formatMoney(liquido.liquido)}</span>
          </div>
          <p className="text-2xs text-muted">
            {liquido.confirmado
              ? 'Já conciliado com a transação real importada da Pluggy.'
              : 'Ainda não conciliado — aguardando o depósito real cair e bater com esse valor.'}
          </p>
        </div>
      )}
    </Modal>
  )
}
